import { InfoIcon } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { formatCurrency } from 'utils/formatCurrency';

import { LTLQuote } from '../types';
import { RateMetadataTooltipContent as TaiRateMetadataTooltipContent } from './Tai/RateCardMetadata';
import { RateMetadataTooltipContent as ThreeGRateMetadataTooltipContent } from './ThreeG/RateCardMetadata';

export type LTLRateMetadata = NonNullable<LTLQuote['rateMetadata']>;

function RateMetadataTooltipContent({
  rateMetadata,
}: {
  rateMetadata: LTLRateMetadata;
}) {
  const isThreeG = rateMetadata.isThreeG === true;

  if (isThreeG) {
    return <ThreeGRateMetadataTooltipContent rateMetadata={rateMetadata} />;
  }

  return <TaiRateMetadataTooltipContent rateMetadata={rateMetadata} />;
}

interface RateMetadataViewProps {
  rateMetadata: LTLRateMetadata;
  transitDays: number;
}

export function RateMetadataView({
  rateMetadata,
  transitDays,
}: RateMetadataViewProps) {
  const displayTransitDays = rateMetadata.transitDays ?? transitDays;

  return (
    <>
      <Flex direction='col' gap='xs'>
        <Flex gap='xs' align='center'>
          <Typography variant='body-sm' weight='semibold'>
            Rates:
          </Typography>
        </Flex>

        <Flex align='start' direction='col' gap='sm' className='min-w-0'>
          <Flex align='center' gap='xs' className='min-w-0 pl-2'>
            <Typography variant='body-sm' className='text-neutral shrink-0'>
              Buy:
            </Typography>
            <Typography variant='body-sm' className='text-success-600'>
              {formatCurrency(rateMetadata.buyRateTotal ?? 0, 'USD', 2)}
            </Typography>
          </Flex>
          <Flex align='center' gap='xs' className='min-w-0 pl-2'>
            <Typography variant='body-sm' className='text-neutral shrink-0'>
              Sell:
            </Typography>
            <Typography variant='body-sm' className='text-success-600'>
              {formatCurrency(rateMetadata.sellRateTotal ?? 0, 'USD', 2)}
            </Typography>
            <Tooltip delayDuration={10}>
              <TooltipTrigger
                asChild
                onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                  e.stopPropagation()
                }
              >
                <InfoIcon className='h-4 w-4 text-neutral-600 cursor-help shrink-0' />
              </TooltipTrigger>
              <TooltipContent
                className='bg-neutral-50 border-neutral-200'
                side='bottom'
              >
                <RateMetadataTooltipContent rateMetadata={rateMetadata} />
              </TooltipContent>
            </Tooltip>
          </Flex>
        </Flex>
      </Flex>

      <Flex gap='xs' align='center' className='mt-2'>
        <Typography variant='body-sm' weight='semibold'>
          Transit:
        </Typography>
        <Typography variant='body-xs' className='text-neutral-700'>
          {displayTransitDays} day{displayTransitDays !== 1 ? 's' : ''}
        </Typography>
      </Flex>
    </>
  );
}
